import { ChangeDetectionStrategy, Component, inject, Input, signal } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatCardContent } from '@angular/material/card'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { CustomSymbologyComponent } from '@components/custom-symbology/custom-symbology.component'
import { URL_LEGEND } from '@core/constants/constant.data'
import { LAYERS_ARRAY } from '@core/constants/layers.data'
import { CMSession, ResultLayerCM } from '@core/models/calculation-module.model'
import { LayerPersonalPayload } from '@core/models/layers.model'
import { CMSessionService } from '@core/services/cm-session.service'
import { UploadedLayersService } from '@core/services/layers-uploaded.service'
import { SessionManagerService } from '@core/services/session-manager.service'
import Keycloak from 'keycloak-js'

@Component({
  selector: 'app-card-layer-cm',
  templateUrl: './card-layer-cm.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    // Components
    CustomSymbologyComponent,
    //Material
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatCardContent,
  ],
})
export class CardLayerCmComponent {
  @Input({ required: true }) layer!: ResultLayerCM
  @Input({ required: true }) format: string = ''
  @Input({ required: true }) cmSession!: CMSession
  readonly keycloak = inject(Keycloak)
  private readonly _cmSessionService = inject(CMSessionService)
  private readonly _uploadedLayersService = inject(UploadedLayersService)
  private readonly _sessionManagerService = inject(SessionManagerService)

  stillSpaceOnAccountState$ = this._uploadedLayersService.stillSpaceOnAccountState$

  isConnected$ = signal<boolean>(false)
  isDownloading$ = signal<boolean>(false)
  isSaving$ = signal<boolean>(false)
  canBeSaveAsPL$ = signal<boolean>(false)

  symbologyUrl = URL_LEGEND

  async ngOnInit() {
    this.isConnected$.set(this.keycloak.authenticated ?? false)
    this.canBeSaveAsPL$.set(LAYERS_ARRAY.some((layer) => this.layer.layer === layer.workspacename))
  }

  centerMap() {
    this._cmSessionService.cmLayerCenterMap(this.layer)
  }

  async downloadCMLayer() {
    this.isDownloading$.set(true)

    const extension = this._cmSessionService.defineExtensionLayer(this.format)
    let path = this.layer.path

    if (extension && !path.endsWith(extension)) {
      path += extension
    }

    const response = await this._cmSessionService.exportCMresultFile(
      path,
      this.format,
      this.cmSession.results.session_folder,
    )

    if (response) {
      const url = URL.createObjectURL(response) as string
      const a = document.createElement('a')
      a.href = url
      a.download = this.layer.name + extension
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
    this.isDownloading$.set(false)
  }

  async saveInPersonalLayer() {
    this.isSaving$.set(true)

    const extension = this._cmSessionService.defineExtensionLayer(this.format)

    let path = this.layer.path
    if (extension && !path.endsWith(extension)) {
      path += extension
    }
    const response = await this._cmSessionService.exportCMresultFile(
      path,
      this.format,
      this.cmSession.results.session_folder,
    )

    if (response == null) return

    const file = new File(
      [response],
      this.cmSession.session_name + ' - ' + this.layer.name + extension,
    )

    const layerInfo: LayerPersonalPayload = {
      workspacename: this.layer.layer ?? '',
      layer_type: this.layer.type,
    }

    this._uploadedLayersService.addPersonalLayer(file, layerInfo).then(() => {
      this._uploadedLayersService.getUserPersonalAndSharedLayers()
      this._sessionManagerService.sizeAccount()
    })

    this.isSaving$.set(false)
  }
}
